import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchWordOfTheDay } from "../../redux/actions/gameActions";
import { RootState, AppDispatch } from "../../redux/store/index";
import {
  typeLetter,
  deleteLetter,
  submitGuess,
  resetGame,
} from "../../redux/slices/gameSlice";
import type { LetterState } from "../../redux/slices/gameSlice";
import styles from "./WordGame.module.css";
import logo from "../../assets/images/logo.png";

const validateWord = async (word: string): Promise<boolean> => {
  try {
    const response = await fetch(`https://words.dev-apis.com/validate-word`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ word: word.toLowerCase() }),
    });
    const data = await response.json();
    return data.validWord;
  } catch (error) {
    console.error("Word validation error:", error);
    return false;
  }
};

const WORD_LENGTH = 5;
const MAX_ATTEMPTS = 6;

export default function WordGame() {
  const dispatch = useDispatch<AppDispatch>();
  const {
    answer,
    guesses,
    results,
    currentGuess,
    gameStatus,
    attempt,
    loading,
  } = useSelector((state: RootState) => state.game);
  const [isValidating, setIsValidating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    dispatch(fetchWordOfTheDay());
  }, [dispatch]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (gameStatus !== "playing") return;

      if (e.key === "Enter") {
        handleSubmit();
      } else if (e.key === "Backspace") {
        dispatch(deleteLetter());
      } else if (/^[a-zA-Z]$/.test(e.key)) {
        dispatch(typeLetter(e.key.toUpperCase()));
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [gameStatus, currentGuess, answer, dispatch]);

  const checkGuess = (guess: string): LetterState[] => {
    const result: LetterState[] = Array(WORD_LENGTH).fill("absent");
    const answerArray = answer.split("");
    const guessArray = guess.split("");
    const usedIndices: boolean[] = Array(WORD_LENGTH).fill(false);

    guessArray.forEach((letter, i) => {
      if (letter === answerArray[i]) {
        result[i] = "correct";
        usedIndices[i] = true;
      }
    });

    guessArray.forEach((letter, i) => {
      if (result[i] === "correct") return;

      const answerIndex = answerArray.findIndex(
        (l, idx) => l === letter && !usedIndices[idx],
      );

      if (answerIndex !== -1) {
        result[i] = "present";
        usedIndices[answerIndex] = true;
      }
    });

    return result;
  };

  const handleSubmit = async () => {
    if (currentGuess.length !== WORD_LENGTH) {
      setErrorMessage("Word must be 5 letters!");
      setTimeout(() => setErrorMessage(""), 2000);
      return;
    }

    setIsValidating(true);
    const isValid = await validateWord(currentGuess);
    setIsValidating(false);

    if (!isValid) {
      setErrorMessage("Write a valid word!");
      setTimeout(() => setErrorMessage(""), 2000);
      return;
    }

    setErrorMessage("");
    const result = checkGuess(currentGuess);
    dispatch(submitGuess(result));
  };

  const handleKeyClick = (key: string) => {
    if (key === "ENTER") {
      handleSubmit();
    } else if (key === "⌫") {
      dispatch(deleteLetter());
    } else {
      dispatch(typeLetter(key));
    }
  };

  const handleReset = () => {
    dispatch(resetGame());
    dispatch(fetchWordOfTheDay());
  };

  const getKeyState = (key: string): LetterState | null => {
    for (let i = 0; i < guesses.length; i++) {
      const guessIndex = guesses[i].indexOf(key);
      if (guessIndex !== -1) {
        const state = results[i][guessIndex];
        if (state === "correct") return "correct";
        if (state === "present") return "present";
        if (state === "absent") return "absent";
      }
    }
    return null;
  };

  const renderGrid = () => {
    const rows = [];
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      const cells = [];
      const isCurrentRow = i === attempt;
      const rowGuess = i < guesses.length ? guesses[i] : "";
      const rowResult = i < results.length ? results[i] : [];

      for (let j = 0; j < WORD_LENGTH; j++) {
        let letter = "";
        let state: LetterState | "empty" = "empty";

        if (i < guesses.length) {
          letter = rowGuess[j] || "";
          state = rowResult[j] || "empty";
        } else if (isCurrentRow) {
          letter = currentGuess[j] || "";
        }

        cells.push(
          <div key={`${i}-${j}`} className={`${styles.cell} ${styles[state]}`}>
            {letter}
          </div>,
        );
      }

      rows.push(
        <div key={i} className={styles.row}>
          {cells}
        </div>,
      );
    }
    return rows;
  };

  const keyboard = [
    ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
    ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
    ["ENTER", "Z", "X", "C", "V", "B", "N", "M", "⌫"],
  ];

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading word...</div>
      </div>
    );
  }

  const handleSubmitInfo = () => {
    setShowInfo(true);
  };

  return (
    <>
      <div className={styles.info}>
        <button onClick={handleSubmitInfo}>?</button>
      </div>
      <div className={styles.container}>
        <header className={styles.header}>
          <h1>
            Online{" "}
            <img src={logo} className={styles.img} alt="Online Kosova logo" />{" "}
            Kosova
          </h1>
        </header>
        {showInfo && (
          <div className={styles.gameInfo}>
            <button
              className={styles.closeButton}
              onClick={() => setShowInfo(false)}
            >
              ×
            </button>
            <h3>Information</h3>
            <p> * Each guess must be a valid 5-letter word</p>
            <p>
              {" "}
              * After each guess, the color of the tiles will change to show how
              close your guess was to the word
            </p>
            <h4>Color Meanings</h4>
            <p>
              🟦 Blue (Correct) - The letter is in the word and in the correct
              position
            </p>
            <p>
              🟨 Yellow (Present) - The letter is in the word but in the wrong
              position
            </p>
            <p>⬛ Gray (Absent) - The letter is not in the word</p>
          </div>
        )}
        {gameStatus !== "playing" && (
          <div className={styles.gameOver}>
            {gameStatus === "won" ? (
              <div className={styles.winMessage}>
                <h2>🎉 Congrats! 🎉</h2>
                <p>You guessed the word in {attempt} attempts!</p>
              </div>
            ) : (
              <div className={styles.loseMessage}>
                <h2>Game Over!</h2>
                {/*<p>The word was: <strong>{answer}</strong></p>*/}
              </div>
            )}
            <button onClick={handleReset} className={styles.resetButton}>
              Play Again
            </button>
          </div>
        )}
        <div className={styles.gameBoard}>
          {errorMessage && (
            <div className={styles.errorMessage}>{errorMessage}</div>
          )}

          {renderGrid()}
        </div>

        <div className={styles.keyboard}>
          {keyboard.map((row, i) => (
            <div key={i} className={styles.keyboardRow}>
              {row.map((key) => {
                const keyState = getKeyState(key);
                return (
                  <button
                    key={key}
                    onClick={() => handleKeyClick(key)}
                    className={`${styles.key} ${
                      key === "ENTER" || key === "⌫" ? styles.specialKey : ""
                    } ${keyState ? styles[keyState] : ""}`}
                    disabled={gameStatus !== "playing"}
                  >
                    {key}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
